import { Fragment, useRef, useState } from "react";
import useSWR from "swr";
import { useLocalStorage } from "@mantine/hooks";
import { Table, Button, Group, Stack, Text, Modal, TextInput, NumberInput, Select, ActionIcon, Alert, Tooltip, Divider, UnstyledButton } from "@mantine/core";
import Plus from "lucide-react/dist/esm/icons/plus";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import Search from "lucide-react/dist/esm/icons/search";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import { useFetch, getSubjects, getSemesters, createSubject, updateSubject, deleteSubject, getReviewSubjects, setLectureReviewState, playlistUrl, Subject, ReviewState } from "../../../shared/api";
import { LectureList } from "../../review/SubjectReviewList";
import { WithHint } from "../../../shared/components/InfoHint";
import { SubjectPlaylists, SubjectPlaylistsHandle, playlistTitle, useChannels } from "./SubjectPlaylists";
import youtubeIcon from "../../../shared/assets/colored/youtube.png";
import vkIcon from "../../../shared/assets/colored/vk.png";
import { StudentSelect } from "../../../shared/components/StudentSelect";
import { ErrorDisplay, Loading, extractLogin } from "../../../shared/utils";
import { FilterBar, useFilterDraft } from "../../../shared/components/FilterBar";
import { PersonName } from "../../../shared/components/PersonName";

const PLATFORM_ICONS = { youtube: youtubeIcon, vk: vkIcon } as const;

interface FormState { semester_id: string | null; title: string; awh_multiplier: number | string; operator_money_coef: number | string; editor_money_coef: number | string; reviewer_id: string; editor_id: string }
const EMPTY: FormState = { semester_id: null, title: "", awh_multiplier: 1, operator_money_coef: 1, editor_money_coef: 2, reviewer_id: "", editor_id: "" };

interface SubjectFilters { search: string; semesterId: string | null; reviewerId: string | null }
const NO_FILTERS: SubjectFilters = { search: "", semesterId: null, reviewerId: null };

export function SubjectsTab() {
  const { fetch } = useFetch();
  // The full list feeds the counter and the reviewer options; the table shows the server-filtered one.
  const { data, error, isLoading, mutate: mutateAll } = useSWR("admin/subjects", () => getSubjects({ fetch }));
  const [applied, setApplied] = useState<SubjectFilters>(NO_FILTERS);
  const { draft, patch, dirty } = useFilterDraft(applied);
  const { data: shown, mutate: mutateShown } = useSWR(["admin/subjects/filtered", applied], () => getSubjects({ search: applied.search.trim(), semesterId: applied.semesterId, reviewerId: applied.reviewerId, fetch }));
  const mutate = () => Promise.all([mutateAll(), mutateShown()]);
  const { data: semesters } = useSWR("admin/semesters", () => getSemesters({ fetch }));
  const [lastSemester, setLastSemester] = useLocalStorage<string | null>({ key: "admin:subjects:last-semester", defaultValue: null, getInitialValueInEffect: false });

  const defaultSemId = () => (lastSemester && (semesters ?? []).some(s => s.id === lastSemester)) ? lastSemester : ((semesters ?? []).find(s => s.is_active)?.id ?? null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const playlistsRef = useRef<SubjectPlaylistsHandle>(null);
  const { data: channels } = useChannels();
  // Each subject unfolds into its lectures, the same list a reviewer sees.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const { data: lectureData, error: lecturesError, mutate: mutateLectures } = useSWR("admin/subject-lectures", () => getReviewSubjects({ role: "admin", fetch }));
  const [lectureError, setLectureError] = useState<string | null>(null);
  const lecturesBySubject = new Map((lectureData ?? []).map(s => [s.id, s.lectures]));
  const toggle = (id: number) => setExpanded(prev => { const next = new Set(prev); if (!next.delete(id)) next.add(id); return next; });

  async function changeReviewState(lectureId: number, state: ReviewState) {
    setLectureError(null);
    try { await setLectureReviewState({ lectureId, reviewState: state, fetch }); await mutateLectures(); }
    catch (e: any) { setLectureError(e?.message ?? String(e)); }
  }

  const semOptions = (semesters ?? []).map(s => ({ value: s.id, label: s.title }));
  const renderPerson = (id: string | null, name?: string | null, archived?: boolean) => id
    ? <Tooltip label={archived ? `${id} · в архиве` : id} openDelay={300} withArrow><span style={{ display: "block", minWidth: 0 }}><PersonName truncate archived={archived} hint={false}>{name || id}</PersonName></span></Tooltip>
    : <Text fz="sm" c="dimmed">—</Text>;

  const openForm = (s?: Subject) => {
    setEditing(s || null);
    setForm(s ? { semester_id: s.semester_id, title: s.title, awh_multiplier: s.awh_multiplier, operator_money_coef: s.operator_money_coef, editor_money_coef: s.editor_money_coef, reviewer_id: s.reviewer_id ?? "", editor_id: s.editor_id ?? "" } : { ...EMPTY, semester_id: defaultSemId() });
    setSaveError(null); setModalOpen(true);
  };

  async function save() {
    setSaving(true); setSaveError(null);
    try {
      const payload = { semester_id: form.semester_id, title: form.title, awh_multiplier: Number(form.awh_multiplier), operator_money_coef: Number(form.operator_money_coef), editor_money_coef: Number(form.editor_money_coef), reviewer_id: form.reviewer_id.trim() || null, editor_id: form.editor_id.trim() || null };
      let id: number;
      if (editing) { await updateSubject({ id: editing.id, patch: payload, fetch }); id = editing.id; }
      else {
        id = (await createSubject({ subject: payload, fetch })).id; setLastSemester(payload.semester_id);
        // From here on the form edits the saved subject, so "retry" won't create a second one.
        setEditing({ ...payload, id, awh_multiplier: payload.awh_multiplier } as Subject);
      }
      const playlistsOk = await (playlistsRef.current?.apply(id) ?? Promise.resolve(true));
      await mutate();
      if (playlistsOk) setModalOpen(false);
      else setSaveError("Предмет сохранён, но с плейлистами не всё получилось — см. ошибки выше. Можно поправить и сохранить ещё раз.");
    } catch (e: any) { setSaveError(e?.message ?? String(e)); }
    finally { setSaving(false); }
  }

  async function remove(s: Subject) {
    if (!window.confirm(`Удалить предмет «${s.title}»?`)) return;
    await deleteSubject({ id: s.id, fetch }); await mutate();
  }

  if (error) return <ErrorDisplay error={error} />;
  if (isLoading || !data) return <Loading />;

  const reviewerNames = new Map(data.filter(s => s.reviewer_id).map(s => [s.reviewer_id!, s.reviewer_name || s.reviewer_id!]));
  const reviewerOptions = Array.from(reviewerNames, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Всего: {data.length}</Text><Button leftSection={<Plus size={16} />} onClick={() => openForm()}>Добавить предмет</Button></Group>
      <FilterBar dirty={dirty} onApply={() => setApplied(draft)} onReset={() => setApplied(NO_FILTERS)}>
        <TextInput placeholder="Поиск по названию" leftSection={<Search size={16} />} value={draft.search} onChange={e => patch({ search: e.target.value })} style={{ flex: "1 1 240px" }} />
        <Select placeholder="Все семестры" data={semOptions} value={draft.semesterId} onChange={v => patch({ semesterId: v })} clearable style={{ flex: "1 1 200px" }} />
        <Select placeholder="Все проверяющие" nothingFoundMessage="Не найдено" data={reviewerOptions} value={draft.reviewerId} onChange={v => patch({ reviewerId: v })} clearable searchable style={{ flex: "1 1 200px" }}
          renderOption={({ option }) => <Group justify="space-between" wrap="nowrap" gap="sm" w="100%"><Text fz="sm">{option.label}</Text><Text fz="xs" c="dimmed">{extractLogin(option.value)}</Text></Group>} />
      </FilterBar>
      {lectureError && <Alert color="red" withCloseButton onClose={() => setLectureError(null)}>{lectureError}</Alert>}
      <Table highlightOnHover layout="fixed">
        <Table.Thead><Table.Tr><Table.Th>Название</Table.Th><Table.Th w={80}><WithHint gap={8} hint="Количество пар в одной публикации: множитель ЧРА">Пары</WithHint></Table.Th><Table.Th w={80}><WithHint gap={8} hint="Денежный коэффициент: оператор / монтажёр">ДК</WithHint></Table.Th><Table.Th w={180}>Проверяющий</Table.Th><Table.Th w={180}>Монтажёр</Table.Th><Table.Th w={80}>Плейлисты</Table.Th><Table.Th w={90} /></Table.Tr></Table.Thead>
        <Table.Tbody>
          {(shown ?? []).map(s => {
            const isExpanded = expanded.has(s.id);
            const lectures = lecturesBySubject.get(s.id);
            return (<Fragment key={s.id}>
            <Table.Tr>
              {/* The semester is in the title anyway; it shows in the edit dialog. Long titles wrap rather than get cut. */}
              <Table.Td>
                <UnstyledButton onClick={() => toggle(s.id)} aria-expanded={isExpanded}>
                  <Group gap={6} wrap="nowrap" align="center">
                    <ChevronRight size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0, transition: "transform 150ms ease", transform: isExpanded ? "rotate(90deg)" : "none" }} />
                    <Text fz="sm">{s.title}</Text>
                  </Group>
                </UnstyledButton>
              </Table.Td>
              <Table.Td>{s.awh_multiplier}</Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>{s.operator_money_coef} / {s.editor_money_coef}</Table.Td>
              <Table.Td>{renderPerson(s.reviewer_id, s.reviewer_name, s.reviewer_archived)}</Table.Td>
              <Table.Td>{renderPerson(s.editor_id, s.editor_name, s.editor_archived)}</Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  {(s.playlists ?? []).map(l => (
                    <Tooltip key={l.platform} label={playlistTitle(channels, l)} openDelay={300} withArrow>
                      <a href={playlistUrl(l.platform, l.channel_id, l.playlist_id)} target="_blank" rel="noopener noreferrer" style={{ display: "flex" }}>
                        <img src={PLATFORM_ICONS[l.platform]} alt={l.platform} style={{ width: 18, height: "auto" }} />
                      </a>
                    </Tooltip>
                  ))}
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <ActionIcon variant="subtle" color="gray" onClick={() => openForm(s)} aria-label="Редактировать"><Pencil size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => remove(s)} aria-label="Удалить"><Trash2 size={16} /></ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
            {isExpanded && (
              <Table.Tr>
                <Table.Td colSpan={7} pl={36}>
                  {lectures ? <LectureList lectures={lectures} editable numbered onChange={changeReviewState} />
                    : lecturesError ? <Text fz="sm" c="red">Не удалось загрузить лекции</Text>
                    : <Text fz="sm" c="dimmed">Загрузка…</Text>}
                </Table.Td>
              </Table.Tr>
            )}
            </Fragment>);
          })}
          {shown && shown.length === 0 && <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" ta="center" py="md">{data.length === 0 ? "Предметов пока нет" : "Ничего не найдено"}</Text></Table.Td></Table.Tr>}
        </Table.Tbody>
      </Table>
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Редактировать предмет" : "Новый предмет"} size="lg">
        <Stack>
          <TextInput label="Название" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <Select label="Семестр" placeholder="Не выбран" data={semOptions} value={form.semester_id} onChange={v => setForm(f => ({ ...f, semester_id: v }))} clearable searchable />
          <NumberInput label="Множитель ЧРА" description="Количество пар в одной публикации. Влияет только на выставление ЧРА (т.е. если ЧРА не ставятся, то пишем 0)." min={0} step={0.5} decimalScale={2} value={form.awh_multiplier} onChange={v => setForm(f => ({ ...f, awh_multiplier: v }))} />
          <Stack gap={4}>
            <Group grow align="flex-start">
              <NumberInput label="ДК оператора" min={0} max={100} allowDecimal={false} allowNegative={false} value={form.operator_money_coef} onChange={v => setForm(f => ({ ...f, operator_money_coef: v }))} />
              <NumberInput label="ДК монтажёра" min={0} max={100} allowDecimal={false} allowNegative={false} value={form.editor_money_coef} onChange={v => setForm(f => ({ ...f, editor_money_coef: v }))} />
            </Group>
            <Text fz="xs" c="dimmed">Денежный коэффициент ЧРА. Не меняйте, если не знаете, что делаете.</Text>
          </Stack>
          <StudentSelect label="Проверяющий" description="(Опционально)" value={form.reviewer_id} onChange={v => setForm(f => ({ ...f, reviewer_id: v }))} />
          <StudentSelect label="Монтажёр" description="(Опционально)" value={form.editor_id} onChange={v => setForm(f => ({ ...f, editor_id: v }))} />
          <Divider />
          <SubjectPlaylists key={editing?.id ?? "new"} ref={playlistsRef} subjectId={editing?.id ?? null} subjectTitle={form.title} />
          {saveError && <Alert color="red">{saveError}</Alert>}
          <Group justify="flex-end"><Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button><Button onClick={save} loading={saving} disabled={!form.title || form.operator_money_coef === "" || form.editor_money_coef === ""}>Сохранить</Button></Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
